import React, { useState } from "react";
import { Mail, Check, Copy } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import type { WorkspaceItem } from "../../types";
import { getErrorMessage } from "../../types";

export interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  departments?: string[];
  workspaces?: WorkspaceItem[];
  onSuccess: () => Promise<void>;
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  slug,
  departments = ["General"],
  workspaces = [],
  onSuccess,
}) => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteDept, setInviteDept] = useState("General");
  const [inviteJobTitle, setInviteJobTitle] = useState("");
  const [inviteWorkspaceId, setInviteWorkspaceId] = useState<number | "">("");
  const [inviteStatus, setInviteStatus] = useState<string | null>(null);
  const [lastCreatedInviteUrl, setLastCreatedInviteUrl] = useState<string | null>(null);
  const [copiedModalInvite, setCopiedModalInvite] = useState(false);

  const resetForm = () => {
    setInviteEmail("");
    setInviteRole("member");
    setInviteDept("General");
    setInviteJobTitle("");
    setInviteWorkspaceId("");
    setInviteStatus(null);
    setLastCreatedInviteUrl(null);
    setCopiedModalInvite(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !inviteEmail || !inviteEmail.includes("@")) {
      setInviteStatus("Error: Please enter a valid email address.");
      return;
    }
    setInviteStatus("Sending invitation...");
    try {
      const { data, error } = await api.createInvitationApiV1B2bSlugInvitesPost({
        path: { slug },
        body: {
          email: inviteEmail.trim().toLowerCase(),
          role: inviteRole,
          department: inviteDept.trim() || undefined,
          job_title: inviteJobTitle.trim() || undefined,
          workspace_id: inviteWorkspaceId ? Number(inviteWorkspaceId) : undefined,
        },
      });
      if (data) {
        const inviteUrl =
          (data as any).invite_url ||
          `${window.location.origin}/invitations/accept?token=${(data as any).uuid}`;
        setLastCreatedInviteUrl(inviteUrl);
        setInviteStatus("Success: Invitation sent!");
        await onSuccess();
      } else {
        setInviteStatus(`Error: ${getErrorMessage(error, "Failed to send invitation")}`);
      }
    } catch (err: any) {
      setInviteStatus(`Error: ${err.message || "Failed to send invitation"}`);
    }
  };

  const availableDepts =
    departments && departments.length > 0
      ? departments
      : ["Executive", "Engineering", "Product", "Design", "Marketing", "Sales", "General"];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Team Member"
      icon={<Mail className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {inviteStatus && (
        <div
          className={`mb-4 p-3 rounded-xl text-xs font-semibold ${
            inviteStatus.startsWith("Success") || inviteStatus.includes("successfully")
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : inviteStatus.startsWith("Error")
              ? "bg-rose-50 border border-rose-200 text-rose-800"
              : "bg-indigo-50 border border-indigo-200 text-indigo-800"
          }`}
        >
          {inviteStatus}
        </div>
      )}

      {lastCreatedInviteUrl ? (
        <div className="space-y-4">
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                Direct Invitation Link
              </span>
              <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                <Check className="w-3 h-3" /> Ready
              </span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              An email has been dispatched. You can also share this direct invitation link with the member:
            </p>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm">
              <input
                type="text"
                readOnly
                value={lastCreatedInviteUrl}
                className="w-full bg-transparent px-2.5 py-1 text-xs text-slate-900 font-mono focus:outline-none select-all truncate"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(lastCreatedInviteUrl);
                  setCopiedModalInvite(true);
                  setTimeout(() => setCopiedModalInvite(false), 2500);
                }}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg flex items-center gap-1 shadow-sm transition flex-shrink-0"
              >
                {copiedModalInvite ? (
                  <>
                    <Check className="w-3 h-3 text-emerald-300" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-sm transition"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">
              Employee Email Address *
            </label>
            <input
              type="email"
              required
              placeholder="colleague@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 mb-1.5">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
              >
                <option value="member">Member</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-1.5">Department</label>
              <select
                value={inviteDept}
                onChange={(e) => setInviteDept(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
              >
                {availableDepts.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1.5 flex items-center justify-between">
              <span>Target Workspace</span>
              <span className="text-[10px] text-indigo-600 font-normal">Auto-enrolls on join</span>
            </label>
            <select
              value={inviteWorkspaceId}
              onChange={(e) => setInviteWorkspaceId(e.target.value ? Number(e.target.value) : "")}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
            >
              <option value="">General (Default Workspace)</option>
              {workspaces.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name} ({ws.is_default ? "Default" : `/${ws.slug}`})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">Job Title (Optional)</label>
            <input
              type="text"
              placeholder="e.g. Senior Software Engineer"
              value={inviteJobTitle}
              onChange={(e) => setInviteJobTitle(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
            />
          </div>

          <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={Boolean(inviteStatus?.includes("Sending"))}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50"
            >
              {inviteStatus?.includes("Sending") ? "Sending..." : "Send Invitation"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};
